import { useState, useEffect, useRef } from "react";
import { ChevronDown, Check, X } from "lucide-react";
import "./dropdown.css";

import PropTypes from "prop-types";

export default function Dropdown({
  options,
  disabled = false,
  placeholder = "Select",
  multiple = false,
  searchable = false,
  value,
  displayValue = "",
  onChange = () => {},
  shouldCloseOnSelect = () => {},
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  let selected = value || "";

  if (multiple) {
    selected = Array.isArray(value) ? value : [];
  }

  const handleSelect = (option) => {
    if (multiple) {
      let updated;
      if (selected.includes(option)) {
        updated = selected.filter((item) => item !== option);
      } else {
        updated = [...selected, option];
        }
        onChange(updated);
      if (shouldCloseOnSelect?.(option, updated)) {
        setOpen(false);
        setSearch("");
      }
    } else {
      onChange && onChange(option);
      setOpen(false);
      setSearch("");
    }
  };

  const removeTag = (option, e) => {
    e.stopPropagation();
    if (multiple) {
      const updated = selected.filter((item) => item !== option);
      onChange(updated);
    }
  };

  // ✅ Close on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setSearch("");
      return;
    }
    if (searchable) {
      searchRef.current?.focus();
    }
  }, [open, searchable]);

  const query = search.trim().toLowerCase();
  const visibleOptions =
    searchable && query
      ? options.filter((option) =>
          String(option).toLowerCase().includes(query),
        )
      : options;

  const renderDropdownValue = () => {
    if (!multiple) {
      return (
        <span className="dropdown-selected" title={selected || undefined}>
          {displayValue || selected || placeholder}
        </span>
      );
    }
  
    if (!selected.length) {
      return placeholder;
    }
  
    return selected.map((s, index) => (
      <span
        key={`${s}-${index}`}
        className="dropdown-tag d-flex align-center"
      >
        {s}
  
        <X
          size={14}
          className="tag-remove"
          onClick={(e) => removeTag(s, e)}
        />
      </span>
    ));
  };
  

  return (
    <div className={`dropdown ${disabled ? "disabled" : ""}`} ref={dropdownRef}>
      <div type="button" className="dropdown-toggle" onClick={() => !disabled &&  setOpen(!open)}>
        <div className="dropdown-value">
          {renderDropdownValue()}
        </div>

        <ChevronDown
          size={18}
          className={`dropdown-arrow ${open ? "open" : ""}`}
        />
      </div>

      {open && (
        <div className="dropdown-menu">
          {searchable && (
            <div className="dropdown-search" onMouseDown={(e) => e.stopPropagation()}>
              <input
                ref={searchRef}
                type="text"
                className="dropdown-search-input"
                value={search}
                placeholder="Search"
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {visibleOptions.length === 0 ? (
            <div className="dropdown-empty">No matches</div>
          ) : (
            visibleOptions.map((option) => {
              const active = multiple
                ? selected.includes(option)
                : selected === option;

              return (
                <div
                  key={option}
                  className={`dropdown-item ${active ? "active" : ""}`}
                  onClick={() => handleSelect(option)}
                >
                  <span>{option}</span>
                  {active && <Check size={16} className="check-icon" />}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}


/* ✅ Prop Validation */
Dropdown.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,

  placeholder: PropTypes.string,

  multiple: PropTypes.bool,

  searchable: PropTypes.bool,

  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),

  displayValue: PropTypes.string,

  onChange: PropTypes.func,

  shouldCloseOnSelect: PropTypes.func,
};